import { split } from 'lodash'
import { AmazonPayService } from '@/services'
import { ENV_KEY } from '@/constants/env'
import store from '@/store'
import paymentMixin from './payment'
import shopMixin from './shop'

const amazonLoginButtonConfig = {
  merchantId: store.getters['env/GET_KEY'](ENV_KEY.AMAZON_PAY_MERCHANT_ID),
  storeId: store.getters['env/GET_KEY'](ENV_KEY.AMAZON_PAY_CLIENT_API),
  sandbox: !!process.env.VUE_APP_AMAZON_SANDBOX,
  ledgerCurrency: process.env.VUE_APP_AMAZON_CURRENCY,
  checkoutLanguage: process.env.VUE_APP_AMAZON_CHECKOUT_LANGUAGE,
  placement: process.env.VUE_APP_AMAZON_PLACMENT,
  buttonColor: process.env.VUE_APP_AMAZON_COLOR_PARAMETER,
}

const otherPayload = {
  scopes: process.env.VUE_APP_AMAZON_SCOPE.split(','),
  chargePermissionTypeOneTime: process.env.VUE_APP_AMAZON_CHARGE_PERMISSION_TYPE_ONETIME,
  chargePermissionTypeRecuring: process.env.VUE_APP_AMAZON_CHARGE_PERMISSION_TYPE_RECURRING,
  frequencyUnit: process.env.VUE_APP_AMAZON_FREQUENCY_UNIT,
  frequencyValue: process.env.VUE_APP_AMAZON_FREQUENCY_VALUE,
  paymentIntent: process.env.VUE_APP_AMAZON_PAYMENT_INTENT,
  paymentIntentConfirm: process.env.VUE_APP_AMAZON_PAYMENT_INTENT_CONFIRM,
}

const generateSignature = (isPeriodic, routeName) => {
  const isSubscriptionDetailPage = routeName === 'EC_LINE015'
  const href = isSubscriptionDetailPage ? window.location.href.split('?')[0] : window.location.href
  const cancelUrl = new URL(href)
  cancelUrl.searchParams.set('cancel_amazon', 'true')

  return {
    storeId: amazonLoginButtonConfig.storeId,
    webCheckoutDetails: {
      checkoutReviewReturnUrl: href,
      checkoutCancelUrl: cancelUrl.toString(),
    },
    scopes: otherPayload.scopes,
    chargePermissionType: isPeriodic
      ? otherPayload.chargePermissionTypeRecuring
      : otherPayload.chargePermissionTypeOneTime,
    recurringMetadata:
      (isPeriodic && {
        frequency: {
          unit: otherPayload.frequencyUnit,
          value: otherPayload.frequencyValue,
        },
      }) ||
      undefined,
    paymentDetails: {
      paymentIntent: otherPayload.paymentIntent,
    },
  }
}

const updatePermissionPayload = (payment_total, isPeriodic, productType) => ({
  webCheckoutDetails: {
    checkoutReviewReturnUrl: window.location.href,
    checkoutResultReturnUrl: window.location.href,
  },
  productType,
  paymentDetails: {
    paymentIntent: otherPayload.paymentIntent,
    canHandlePendingAuthorization: false,
    chargeAmount: {
      amount: String(payment_total),
      currencyCode: amazonLoginButtonConfig.ledgerCurrency,
    },
  },
  chargePermissionType: isPeriodic
    ? otherPayload.chargePermissionTypeRecuring
    : otherPayload.chargePermissionTypeOneTime,
  recurringMetadata:
    (isPeriodic && {
      frequency: {
        unit: otherPayload.frequencyUnit,
        value: otherPayload.frequencyValue,
      },
      amount: null,
    }) ||
    undefined,
})

const updatePermissionConfirmPayload = () => ({
  webCheckoutDetails: {
    checkoutReviewReturnUrl: window.location.href,
    checkoutResultReturnUrl: window.location.href,
  },
  paymentDetails: {
    paymentIntent: otherPayload.paymentIntentConfirm,
  },
  chargePermissionType: otherPayload.chargePermissionTypeRecuring,
  recurringMetadata: {
    frequency: {
      unit: otherPayload.frequencyUnit,
      value: otherPayload.frequencyValue,
    },
  },
})

export default {
  mixins: [paymentMixin, shopMixin],
  computed: {
    amazonProductType() {
      return (
        (this.user && process.env.VUE_APP_AMAZON_PRODUCT_TYPE_PAY_ONLY) ||
        process.env.VUE_APP_AMAZON_PRODUCT_TYPE_PAY_AND_SHIP
      )
    },
  },
  methods: {
    /**
     * Handle click button amazon
     * @param {*} event
     * @returns false if over max amount or zero, true if opposite
     */
    handleAmazonButton(event, backupKey, editSubscription = false) {
      if (this.disableAmazon) {
        event.preventDefault()
        event.stopImmediatePropagation()
        return false
      }

      !!this.form && (this.$session[backupKey] = JSON.stringify(this.form))
      if (editSubscription) {
        this.saveData()
      }

      return true
    },
    /**
     * Get signature and render amazon pay button
     */
    getAmazonButton(productType, button = ['#AmazonPayButton']) {
      return new Promise((resolve, reject) => {
        const payload = generateSignature(this.isPeriodic, this.$route.name)

        if (!this.isLoginAmazon) {
          AmazonPayService.getSignature(payload)
            .then(({ data }) => {
              button.forEach((buttonId) => {
                // eslint-disable-next-line no-undef
                amazon.Pay.renderButton(buttonId, {
                  ...amazonLoginButtonConfig,
                  productType,
                  createCheckoutSessionConfig: {
                    payloadJSON: JSON.stringify(payload), // payload generated in step 2
                    signature: data.signature,
                    publicKeyId: store.getters['env/GET_KEY'](ENV_KEY.AMAZON_PAY_PUBLIC_KEY),
                  },
                })
              })
            })
            .then(() => resolve())
            .catch((err) => reject(err))
        } else {
          resolve()
        }
      })
    },

    /**
     * Hidden checkout session id
     *
     */
    initAmazonSessionId(accessToken) {
      const query = { ...this.$route.query }
      const isCancelAmazonPay = /^true$/i.test(query?.cancel_amazon)

      if (isCancelAmazonPay) {
        this.onAmazonLogout(accessToken)
        delete query.cancel_amazon
        delete query?.amazonCheckoutSessionId
        this.$router.replace({ query })
        return Promise.resolve({ isLogoutAmazon: true })
      }

      if (this.$route.query.amazonCheckoutSessionId) {
        this.$session[accessToken] = this.$route.query.amazonCheckoutSessionId
        delete query.amazonCheckoutSessionId
        return this.$router.replace({ query })
      } else {
        return Promise.resolve({ isLogoutAmazon: false })
      }
    },

    /**
     * Binding action for button change address/payment amazon
     */
    bindingAction(id, backupForm, accessToken, backupKey) {
      if (this.isLoginAmazon) {
        // '#changeCustomerAddress'
        //  eslint-disable-next-line no-undef
        amazon.Pay.bindChangeAction(`#${id}`, {
          amazonCheckoutSessionId: this.$session[accessToken],
          changeAction: 'changeAddress',
        })

        document.getElementById(id).addEventListener('click', function (event) {
          event.preventDefault()

          localStorage.setItem(backupKey, JSON.stringify(backupForm))
          return true
        })
      }
    },

    /**
     * Hanlde click logout button
     */
    onClickAmazonLogout(productType, button = ['#AmazonPayButton'], accessToken) {
      this.onAmazonLogout(accessToken)
      // window.location.reload()
      this.getAmazonButton(productType, button)
    },

    /**
     * Handle logout
     */
    onAmazonLogout(accessToken) {
      try {
        // eslint-disable-next-line no-undef
        amazon.Pay.signout()

        // Remove amazon token
        this.$session[accessToken] = null

        // Set default payment method to credit card
      } catch (error) {
        console.log(error)
      }
    },

    /**
     * Hanlde logout after finish order
     */

    /**
     * Handle get amazon address
     */
    getAmazonShiping(accessToken) {
      if (this.isLoginAmazon) {
        return AmazonPayService.getAmazonCustomerInfo(this.$session[accessToken])
      } else {
        return Promise.resolve({})
      }
    },

    /**
     * Handle fill amazon infor to form
     */
    fillAmazonCustomerInfo(amazonForm, form) {
      // Update address
      form.prefecture =
        this.prefectures.find((item) => item.name === amazonForm.billingAddress.stateOrRegion)?.id || null
      form.municipality = amazonForm.billingAddress.addressLine1
      form.address = amazonForm.billingAddress.addressLine2
      form.buildingApartmentName = amazonForm.billingAddress.addressLine3

      // Update phone number
      form.phoneNumber = amazonForm.buyer.phoneNumber

      // Update name
      const [name01, name02] = split(amazonForm.buyer.name, /[\p{Zs}\s]/, 2)
      form.lastName = name01
      form.firstName = name02 || null
      form.lastNameKana = null
      form.firstNameKana = null

      // Update postal code
      let postalCode = amazonForm.billingAddress.postalCode
      if (!amazonForm.billingAddress.postalCode.includes(this.$t('common.hyphenSeparator'))) {
        const _code = amazonForm.billingAddress.postalCode.split('')
        _code.splice(3, 0, this.$t('common.hyphenSeparator'))
        postalCode = _code.join('')
      }

      const [postalCode1, postalCode2] = postalCode.split(this.$t('common.hyphenSeparator'))
      form.postalCode1 = postalCode1
      form.postalCode2 = postalCode2

      // Update email
      form.email = amazonForm.buyer.email
      form.emailConfirm = amazonForm.buyer.email

      // Update sex_id
      form.birthdayYear = null
      form.birthdayMonth = null
      form.birthdayDay = null
      form.sex = null
    },

    fillAmazonAddressInfo(amazonForm, form) {
      // Update address
      form.prefecture =
        this.prefectures.find((item) => item.name === amazonForm.shippingAddress.stateOrRegion)?.id || null
      form.municipality = amazonForm.shippingAddress.addressLine1
      form.address = amazonForm.shippingAddress.addressLine2
      form.buildingApartmentName = amazonForm.shippingAddress.addressLine3

      // Update phone number
      form.phoneNumber = amazonForm.shippingAddress.phoneNumber

      // Update name
      const [name01, name02] = split(amazonForm.shippingAddress.name, /[\p{Zs}\s]/, 2)
      form.lastName = name01
      form.firstName = name02 || null
      form.lastNameKana = null
      form.firstNameKana = null

      // Update postal code
      let postalCode = amazonForm.shippingAddress.postalCode
      if (!amazonForm.shippingAddress.postalCode.includes(this.$t('common.hyphenSeparator'))) {
        const _code = amazonForm.shippingAddress.postalCode.split('')
        _code.splice(3, 0, this.$t('common.hyphenSeparator'))
        postalCode = _code.join('')
      }

      const [postalCode1, postalCode2] = postalCode.split(this.$t('common.hyphenSeparator'))
      form.postalCode1 = postalCode1
      form.postalCode2 = postalCode2
    },

    /**
     * Handle fill amazon payment to form
     */
    fillAmazonPaymentInfo(amazonForm, form, replace = true) {
      form.amazonCard = replace
        ? amazonForm.paymentPreferences[0]?.paymentDescriptor.replaceAll(/[*]| \(Amazon Pay\)/gi, '').trim()
        : amazonForm.paymentPreferences[0]?.paymentDescriptor
    },

    /**
     * Handle update permision authorize
     */
    updateAmazonPermission(payment_total, accessToken) {
      const form = updatePermissionPayload(payment_total)
      return AmazonPayService.updateAmazonSession(this.$session[accessToken], form)
    },

    /**
     * Hanlde update amazon when update subscription
     */
    updateAmazonConfirm(accessToken) {
      const form = updatePermissionConfirmPayload()
      return AmazonPayService.updateAmazonSession(this.$session[accessToken], form)
    },

    isSameBillingAndShipping(data) {
      return (
        data.billingAddress.addressLine1 === data.shippingAddress.addressLine1 &&
        data.billingAddress.addressLine2 === data.shippingAddress.addressLine2 &&
        data.billingAddress.addressLine3 === data.shippingAddress.addressLine3
      )
    },
  },
}
